// API client cổng khách — chấm điểm ê-kíp + tiền tip (spec 2026-08-20,
// quyết định 0032). Cùng session cookie SĐT với portal đơn hàng.
import { serverErrorCode, serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";

const apiBaseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8080/api/v1";

export type ReviewRosterPerson = components["schemas"]["ReviewRosterPerson"];
export type ReviewContext = components["schemas"]["ReviewContextResponse"];
export type ReviewSubmitInput = components["schemas"]["ReviewSubmitInput"];
export type ReviewSubmitResponse = components["schemas"]["ReviewSubmitResponse"];
export type TipInfo = components["schemas"]["TipInfoResponse"];

/** Lỗi API review/tip — giữ code BE để FE phân nhánh (locked → 409). */
export class ReviewApiError extends Error {
  code: string;
  status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = "ReviewApiError";
    this.code = code;
    this.status = status;
  }
}

async function reviewErrorFromResponse(response: Response, fallback: string): Promise<ReviewApiError> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    // Giữ fallback.
  }
  return new ReviewApiError(serverErrorMessage(body, fallback), serverErrorCode(body) || "unknown", response.status);
}

export async function fetchReviewContext(orderId: string, init?: { signal?: AbortSignal }): Promise<ReviewContext> {
  const response = await fetch(`${apiBaseUrl}/public/customer-orders/${orderId}/review-context`, {
    credentials: "include",
    signal: init?.signal ?? null,
  });
  if (!response.ok) throw await reviewErrorFromResponse(response, "Không tải được thông tin chấm điểm");
  return (await response.json()) as ReviewContext;
}

export async function submitReview(orderId: string, input: ReviewSubmitInput): Promise<ReviewSubmitResponse> {
  const response = await fetch(`${apiBaseUrl}/public/customer-orders/${orderId}/review`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw await reviewErrorFromResponse(response, "Không gửi được đánh giá");
  return (await response.json()) as ReviewSubmitResponse;
}

export async function fetchTipInfo(orderId: string, init?: { signal?: AbortSignal }): Promise<TipInfo> {
  const response = await fetch(`${apiBaseUrl}/public/customer-orders/${orderId}/tip-info`, {
    credentials: "include",
    signal: init?.signal ?? null,
  });
  if (!response.ok) throw await reviewErrorFromResponse(response, "Không tải được thông tin gửi tặng");
  return (await response.json()) as TipInfo;
}

/**
 * Dấu thiết bị phục vụ hậu kiểm (spec §5.5) — sinh 1 lần, giữ trong
 * localStorage. Không phải định danh cá nhân; chỉ để màn Soi đánh giá nhận
 * ra 1 máy chấm cho nhiều SĐT.
 */
export function reviewDeviceId(): string {
  const key = "gnpf_review_device";
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const generated = crypto.randomUUID();
    localStorage.setItem(key, generated);
    return generated;
  } catch {
    return "";
  }
}
